import { z } from 'zod';

/**
 * Fiyat takvimi.
 *
 * Tasarim kaynagi: Claude Design - "otelcms Fiyat Takvimi.dc.html".
 */

const tarihDeseni = /^\d{4}-\d{2}-\d{2}$/;
const tarihSema = z.string().regex(tarihDeseni, 'Tarih YYYY-AA-GG biçiminde olmalı');

/**
 * `baslangic` ve `bitis` DAHIL gun listesi.
 *
 * UTC uzerinden yurur: gun sinirinda yerel saat farki yuzunden bir gun
 * atlanmasin diye. Takvim gunleri zaten saat tasimaz.
 */
export function gunAralik(baslangic: string, bitis: string): string[] {
  if (!tarihDeseni.test(baslangic) || !tarihDeseni.test(bitis)) return [];
  const bas = Date.parse(`${baslangic}T00:00:00Z`);
  const son = Date.parse(`${bitis}T00:00:00Z`);
  if (Number.isNaN(bas) || Number.isNaN(son) || son < bas) return [];

  const gunler: string[] = [];
  for (let t = bas; t <= son; t += 86_400_000) {
    gunler.push(new Date(t).toISOString().slice(0, 10));
  }
  return gunler;
}

/**
 * Airbnb tarzi toplu fiyat degisikligi.
 *
 * Fiyat MUTLAK (`fiyatKurus`) ya da GORELI (`yuzde`) verilir; ikisi birden
 * verilemez, cunku "₺2400 yap ve %10 artir" tek bir niyet degildir.
 * `minKalis` ve `satisaKapali` bagimsiz olarak, fiyata dokunmadan da
 * degistirilebilir - "bu hafta sonu satisa kapat" tek islemdir.
 */
export const fiyatTopluSchema = z
  .object({
    ratePlanId: z.string().min(1),
    baslangic: tarihSema,
    bitis: tarihSema,
    /** 0=Pazar … 6=Cumartesi. Bos/atlanmis = araligin tum gunleri. */
    gunler: z.array(z.number().int().min(0).max(6)).max(7).optional(),
    fiyatKurus: z.number().int().min(0).max(100_000_000).optional(),
    /** -90 … +300; mevcut fiyata goreli uygulanir. */
    yuzde: z.number().min(-90).max(300).optional(),
    minKalis: z.number().int().min(1).max(30).optional(),
    satisaKapali: z.boolean().optional(),
  })
  .refine((v) => !(v.fiyatKurus !== undefined && v.yuzde !== undefined), {
    message: 'Fiyat ya sabit ya da yüzde olarak verilir, ikisi birden değil',
    path: ['yuzde'],
  })
  .refine(
    (v) =>
      v.fiyatKurus !== undefined ||
      v.yuzde !== undefined ||
      v.minKalis !== undefined ||
      v.satisaKapali !== undefined,
    { message: 'Değiştirilecek bir alan seçin', path: ['fiyatKurus'] },
  )
  .refine((v) => v.bitis >= v.baslangic, {
    message: 'Bitiş tarihi başlangıçtan önce olamaz',
    path: ['bitis'],
  });
export type FiyatToplu = z.infer<typeof fiyatTopluSchema>;

/** Toplu ekranindaki gun suzgeci dugmeleri. */
export const HAFTA_GUNLERI = [
  { gun: 1, kisa: 'Pzt', ad: 'Pazartesi' },
  { gun: 2, kisa: 'Sal', ad: 'Salı' },
  { gun: 3, kisa: 'Çar', ad: 'Çarşamba' },
  { gun: 4, kisa: 'Per', ad: 'Perşembe' },
  { gun: 5, kisa: 'Cum', ad: 'Cuma' },
  { gun: 6, kisa: 'Cmt', ad: 'Cumartesi' },
  { gun: 0, kisa: 'Paz', ad: 'Pazar' },
] as const;

/** Tasarimdaki "hafta sonu" kisayolu: Cuma + Cumartesi. */
export const HAFTA_SONU = [5, 6];
